import { useCallback, useMemo, useState, useRef } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import useSWR from 'swr';
import useDebounce from 'hooks/useDebounce';
import useColumnPagination from 'hooks/useColumnPagination';
import useFiltersState from 'state/useFiltersState';
import TrelloColumn from '../TrelloColumn/TrelloColumn';
import { COLUMN_TYPE } from '../TrelloColumn/TrelloColumnTypes';
import { TbordOneToneIcon } from 'components/NudosComponents/BordOneToneIcon/BordOneToneIcon.types';
import { IProductList, ORDER_STATUS_ID } from 'types/orders';
import { SORT_ORDER } from 'types/global';
import FinanceCard from 'components/CardInOrderList/FinanceCard/FinanceCard';
import OrdersTrackingFinanceFilters from '../OrdersFinanceTrackingFilters/OrdersTrackingFinanceFilters';
import useOrdersFinancesModule from './useOrdersFinancesModule';
import { getOrdersByStatus } from 'services/orders';
import OrdersCancelledModal from '../OrdersCancelledModal/OrdersCancelledModal';
import ButtonCancelOrders from '../ButtonCancelOrders/ButtonCancelOrders';
import {
  mockPendingOrders,
  mockConfirmedOrders,
  mockDeliveredOrders,
  mockCancelledOrders
} from './ordersFinancesMocks.temp';
import { optionsParamsSwr } from 'utils/optionsParamsSwr';
import UnsuccessfulSearch from 'components/unsuccessfulSearch';
import './OrdersListingFinances.scss';

const DEBOUNCE_DURATION = 1500;

/**
 * Componente que muestra el listado de órdenes en formato de finanzas con columnas tipo Trello.
 * Cada columna realiza su propio llamado al API para obtener las órdenes filtradas por estado.
 */
const OrdersListingFinances = () => {
  const location = useLocation();
  const history = useHistory();
  const { ordersSearchbarText, propertyToFilterSearch } = useFiltersState();
  const { orderFilters, sortFilter } = useOrdersFinancesModule();

  const [openCancelOrdersModal, setOpenCancelOrdersModal] = useState(false);
  const [countryCode, setCountryCode] = useState<string>('');
  const [organizationName, setOrganizationName] = useState<string>('');

  const debouncedSearchText = useDebounce(ordersSearchbarText, DEBOUNCE_DURATION);

  const paginationPending = useColumnPagination();
  const paginationConfirmed = useColumnPagination();
  const paginationDelivered = useColumnPagination();

  const searchQueryParam = useMemo(() => {
    if (!debouncedSearchText || !propertyToFilterSearch?.propertyToFilter) return '';
    const searchObject = encodeURIComponent(
      JSON.stringify({
        name: debouncedSearchText.trim(),
        propertyToFilter: propertyToFilterSearch?.propertyToFilter
      })
    );
    return `search=${searchObject}`;
  }, [debouncedSearchText, propertyToFilterSearch?.propertyToFilter]);

  const filtersQueryParamString = useMemo(() => {
    const filteredFilters = orderFilters?.filter(filter => filter.name !== 'sort') || [];
    return filteredFilters?.length > 0 ? JSON.stringify(filteredFilters) : '';
  }, [orderFilters]);

  const filtersQueryParam = useMemo(() => {
    return filtersQueryParamString ? `filters=${filtersQueryParamString}` : '';
  }, [filtersQueryParamString]);

  const sortQueryParam = useMemo(() => {
    const sortFilterFromOrderFilters = orderFilters?.find(filter => filter.name === 'sort');
    const propertyToSort = sortFilterFromOrderFilters?.values?.[0] || 'orderId';

    return `sort=${JSON.stringify({
      value: sortFilter?.value || SORT_ORDER.DESC,
      propertyToSort: propertyToSort
    })}`;
  }, [sortFilter, orderFilters]);

  const countryQueryParam = useMemo(() => {
    return countryCode ? `country=${encodeURIComponent(countryCode)}` : '';
  }, [countryCode]);

  const organizationQueryParam = useMemo(() => {
    return organizationName ? `organization=${encodeURIComponent(organizationName)}` : '';
  }, [organizationName]);

  const debouncedFiltersQuery = useDebounce(filtersQueryParam, DEBOUNCE_DURATION);

  const prevQueryParamsRef = useRef<string>('');

  const stableQueryParams = useMemo(() => {
    const hasSearch = !!searchQueryParam?.length;
    const hasFilters = !!debouncedFiltersQuery?.length;
    const hasCountry = !!countryQueryParam?.length;
    const hasOrganization = !!organizationQueryParam?.length;
    const hasSort = !!sortQueryParam?.length;

    const queryParts: string[] = [];
    if (hasFilters) queryParts.push(debouncedFiltersQuery);
    if (hasSearch) queryParts.push(searchQueryParam);
    if (hasCountry) queryParts.push(countryQueryParam);
    if (hasOrganization) queryParts.push(organizationQueryParam);
    if (hasSort) queryParts.push(sortQueryParam);

    if (queryParts?.length === 0) {
      const result = '';
      if (prevQueryParamsRef.current === result) return prevQueryParamsRef.current;
      prevQueryParamsRef.current = result;
      return result;
    }

    const result = `?${queryParts.join('&')}`;

    if (prevQueryParamsRef.current === result) return prevQueryParamsRef.current;
    prevQueryParamsRef.current = result;
    return result;
  }, [searchQueryParam, debouncedFiltersQuery, countryQueryParam, organizationQueryParam, sortQueryParam]);

  const queryParams = stableQueryParams;

  const swrKeyPending = useMemo(
    () => (queryParams ? ['orders-finances-pending', ORDER_STATUS_ID.PENDING, queryParams] : null),
    [queryParams]
  );

  const swrKeyConfirmed = useMemo(
    () => (queryParams ? ['orders-finances-confirmed', ORDER_STATUS_ID.ACTIVE, queryParams] : null),
    [queryParams]
  );

  const swrKeyDelivered = useMemo(
    () => (queryParams ? ['orders-finances-delivered', ORDER_STATUS_ID.DELIVERED, queryParams] : null),
    [queryParams]
  );

  const swrKeyCancelled = useMemo(
    () => (queryParams ? ['orders-finances-cancelled', ORDER_STATUS_ID.CANCELLED, queryParams] : null),
    [queryParams]
  );
  // Se esta usando el getOrdersByStatus para obtener las órdenes filtradas por estado pero puede que mas adelante el backend implemente endpoints específicos para cada estado.
  const { data: pendingOrders = [], isLoading: isLoadingPending } = useSWR(
    swrKeyPending,
    () => getOrdersByStatus(ORDER_STATUS_ID.PENDING, queryParams),
    optionsParamsSwr()
  );

  const { data: confirmedOrders = [], isLoading: isLoadingConfirmed } = useSWR(
    swrKeyConfirmed,
    () => getOrdersByStatus(ORDER_STATUS_ID.ACTIVE, queryParams),
    optionsParamsSwr()
  );

  const { data: deliveredOrders = [], isLoading: isLoadingDelivered } = useSWR(
    swrKeyDelivered,
    () => getOrdersByStatus(ORDER_STATUS_ID.DELIVERED, queryParams),
    optionsParamsSwr()
  );

  const { data: cancelledOrders = [] } = useSWR(
    swrKeyCancelled,
    () => getOrdersByStatus(ORDER_STATUS_ID.CANCELLED, queryParams),
    optionsParamsSwr()
  );

  /**
   * Formatea las órdenes agregando un ID como string.
   * @param ordersList - Lista de órdenes a formatear
   * @returns Nueva lista de órdenes con ID formateado
   */
  const formatOrders = (ordersList: IProductList[]): IProductList[] => {
    return ordersList.map(order => ({
      ...order,
      id: JSON.stringify(order.orderId)
    }));
  };

  const formattedPendingOrders = useMemo(() => {
    const formatted = formatOrders(pendingOrders);
    return formatted && formatted.length > 0 ? formatted : formatOrders(mockPendingOrders);
  }, [pendingOrders]);

  const formattedConfirmedOrders = useMemo(() => {
    const formatted = formatOrders(confirmedOrders);
    return formatted && formatted.length > 0 ? formatted : formatOrders(mockConfirmedOrders);
  }, [confirmedOrders]);

  const formattedDeliveredOrders = useMemo(() => {
    const formatted = formatOrders(deliveredOrders);
    return formatted && formatted.length > 0 ? formatted : formatOrders(mockDeliveredOrders);
  }, [deliveredOrders]);

  const formattedCancelledOrders = useMemo(() => {
    const formatted = formatOrders(cancelledOrders);
    return formatted && formatted.length > 0 ? formatted : formatOrders(mockCancelledOrders);
  }, [cancelledOrders]);

  const listOrdersIsPendingForConfirmation = useMemo(
    () => formattedPendingOrders.slice(0, paginationPending.visibleCount),
    [formattedPendingOrders, paginationPending.visibleCount]
  );

  const listOrdersIsConfirmed = useMemo(
    () => formattedConfirmedOrders.slice(0, paginationConfirmed.visibleCount),
    [formattedConfirmedOrders, paginationConfirmed.visibleCount]
  );

  const listOrdersIsDelivered = useMemo(
    () => formattedDeliveredOrders.slice(0, paginationDelivered.visibleCount),
    [formattedDeliveredOrders, paginationDelivered.visibleCount]
  );

  /**
   * Alterna la visibilidad del modal de órdenes canceladas.
   */
  const handleOpenOrCloseCancelOrdersModal = () => {
    setOpenCancelOrdersModal(!openCancelOrdersModal);
  };

  /**
   * Maneja los parámetros de consulta de la URL.
   * @param queryString - String de consulta a procesar
   */
  const handleSetQueryParams = useCallback(
    (queryString: string) => {
      const normalized = queryString.startsWith('?') ? queryString.slice(1) : queryString;
      const params = new URLSearchParams(normalized);
      const countryParam = params.get('country');
      const organizationParam = params.get('organization');

      setCountryCode(countryParam ? decodeURIComponent(countryParam) : '');
      setOrganizationName(organizationParam ? decodeURIComponent(organizationParam) : '');

      history.replace({ pathname: location.pathname, search: normalized ? `?${normalized}` : '' });
    },
    [history, location.pathname]
  );

  const isEmptyItems =
    listOrdersIsPendingForConfirmation?.length === 0 &&
    listOrdersIsConfirmed?.length === 0 &&
    listOrdersIsDelivered?.length === 0 &&
    formattedCancelledOrders?.length === 0;

  const columns = [
    {
      title: 'Pendiente de confirmación',
      subtitle: 'Esperando pago y aprobación de la orden',
      variantIcon: 'clockCircle' as TbordOneToneIcon,
      items: listOrdersIsPendingForConfirmation,
      isLoading: isLoadingPending,
      isLoadingMoreItems: paginationPending.isLoadingMore,
      hasMoreItems: paginationPending.hasMoreItems(formattedPendingOrders.length),
      onLoadMore: paginationPending.handleLoadMore,
      columnType: COLUMN_TYPE.WARNING
    },
    {
      title: 'Confirmado',
      subtitle: 'Orden lista para comenzar a gestionar',
      variantIcon: 'check' as TbordOneToneIcon,
      items: listOrdersIsConfirmed,
      isLoading: isLoadingConfirmed,
      isLoadingMoreItems: paginationConfirmed.isLoadingMore,
      hasMoreItems: paginationConfirmed.hasMoreItems(formattedConfirmedOrders.length),
      onLoadMore: paginationConfirmed.handleLoadMore
    },
    {
      title: 'Entregado',
      subtitle: 'Todos los envíos fueron completados',
      variantIcon: 'doubleCheck' as TbordOneToneIcon,
      items: listOrdersIsDelivered,
      isLoading: isLoadingDelivered,
      isLoadingMoreItems: paginationDelivered.isLoadingMore,
      hasMoreItems: paginationDelivered.hasMoreItems(formattedDeliveredOrders.length),
      onLoadMore: paginationDelivered.handleLoadMore,
      columnType: COLUMN_TYPE.SUCCESS
    }
  ];

  return (
    <div id="ordersListingFinances">
      <div className="containerHeaderFinances">
        <OrdersTrackingFinanceFilters />
        <ButtonCancelOrders
          handleClick={handleOpenOrCloseCancelOrdersModal}
          buttonText={`Ver canceladas`}
          isDisabled={
            formattedCancelledOrders?.length === 0 || isLoadingPending || isLoadingConfirmed || isLoadingDelivered
          }
        />
      </div>
      {isEmptyItems ? (
        <UnsuccessfulSearch customText="No hay resultados para tu búsqueda, revisa el número de orden o prueba con otro tipo de búsqueda" />
      ) : (
        <>
          <div className="columnsContainerFinances">
            {columns?.map(column => (
              <TrelloColumn
                key={column.title}
                title={column.title}
                subtitle={column.subtitle}
                variantIcon={column.variantIcon}
                items={column?.items}
                isLoading={column?.isLoading}
                columnType={column.columnType}
                renderItem={item => (
                  <FinanceCard
                    key={item?.orderId}
                    data={item}
                    refreshData={() => {}}
                    queryParams={queryParams}
                    setQueryParams={handleSetQueryParams}
                  />
                )}
                onLoadMore={column?.onLoadMore}
                hasMoreItems={column?.hasMoreItems}
                isLoadingMoreItems={column?.isLoadingMoreItems}
              />
            ))}
          </div>
          {openCancelOrdersModal && (
            <OrdersCancelledModal isOpen={openCancelOrdersModal} onClose={handleOpenOrCloseCancelOrdersModal} />
          )}
        </>
      )}
    </div>
  );
};

export default OrdersListingFinances;
